import React from 'react';
import { connect } from 'react-redux';
import './api-showcase.styl';
// text
import { ApiShowcaseText as ApiShowcaseTextEN } from '../../input-text/en/landing_page';
import { ApiShowcaseText as ApiShowcaseTextNL } from '../../input-text/nl/landing_page';

// config
const baseUrl = require('Config').demoApiUrl;
const token = require('Config').token;

class ApiShowcase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            countries: [],
            cities: [],
            selectedCountry: null,
            selectedAdminLevel: null,
            selectedCity: null,
            apiResponse: null,
            loadingCountries: false,
            loadingCities: false,
            loadingApiData: false,
            countrySearchTerm: '',
            citySearchTerm: '',
            error: null,
            showCountryDropdown: false,
            showAdminLevelDropdown: false,
            showCityDropdown: false
        };
        this.setLocale = this.setLocale.bind(this);
    }

    componentDidMount() {
        this.fetchCountries();
        // Set initial admin level to LAU2
        const initialAdminLevel = this.getAdminLevels()[0];
        this.setState({ selectedAdminLevel: initialAdminLevel });
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.setState({
                showCountryDropdown: false,
                showAdminLevelDropdown: false,
                showCityDropdown: false
            });
        }
    };

    setWrapperRef = (node) => {
        this.wrapperRef = node;
    };

    setLocale(locale) {
        this.text = null;
        switch (this.props.locale) {
            case 'en':
                this.text = ApiShowcaseTextEN;
                break;
            case 'nl':
                this.text = ApiShowcaseTextNL;
                break;
            default:
                this.text = ApiShowcaseTextEN;
        }
    }

    getAdminLevels = () => {
        // Use the text from the current locale
        if (!this.text) {
            this.setLocale(this.props.locale);
        }
        
        return [
            { code: 'LAU2', name: this.text.adminLevels.LAU2 },
            { code: 'NUTS3', name: this.text.adminLevels.NUTS3 },
            { code: 'NUTS2', name: this.text.adminLevels.NUTS2 },
        ];
    };

    fetchCountries = async () => {
        this.setState({ loadingCountries: true, error: null });
        try {
            const response = await fetch(`${baseUrl}/territory/countries`);
            if (!response.ok) throw new Error('Failed to fetch countries');
            const data = await response.json();
            const sortedData = data.sort((a, b) => a.name[0].localeCompare(b.name[0]));
            this.setState({ countries: sortedData, loadingCountries: false });
        } catch (error) {
            this.setState({
                error: this.text.errorCountries,
                loadingCountries: false
            });
        }
    };

    fetchCities = async (countryCode, adminLevel) => {
        this.setState({ loadingCities: true, error: null, cities: [], selectedCity: null, apiResponse: null });
        try {
            const response = await fetch(`${baseUrl}/territory/${countryCode}/${adminLevel}/names`);
            if (!response.ok) throw new Error('Failed to fetch cities');
            const data = await response.json();
            const sortedData = data.sort((a, b) => a.name[0].localeCompare(b.name[0]));
            this.setState({ cities: sortedData, loadingCities: false });
        } catch (error) {
            this.setState({
                error: this.text.errorCities,
                loadingCities: false
            });
        }
    };

    fetchApiData = async (countryCode, adminLevel, cityName) => {
        this.setState({ loadingApiData: true, error: null });
        try {
            const response = await fetch(`${baseUrl}/territory/${countryCode}/${adminLevel}/name/${encodeURIComponent(cityName)}`, {
                headers: {
                    'x-access-token': token,
                }
            });
            if (!response.ok) throw new Error('Failed to fetch API data');
            const data = await response.json();
            this.setState({ apiResponse: data, loadingApiData: false });
        } catch (error) {
            this.setState({
                error: this.text.errorApiData,
                loadingApiData: false
            });
        }
    };

    handleCountrySelect = (country) => {
        this.setState({
            selectedCountry: country,
            countrySearchTerm: '',
            citySearchTerm: '',
            selectedCity: null,
            apiResponse: null,
            showCountryDropdown: false
        });
        this.fetchCities(country.id, this.state.selectedAdminLevel.code);
    };

    handleAdminLevelSelect = (adminLevel) => {
        this.setState({
            selectedAdminLevel: adminLevel,
            citySearchTerm: '',
            selectedCity: null,
            apiResponse: null,
            showAdminLevelDropdown: false
        });
        if (this.state.selectedCountry) {
            this.fetchCities(this.state.selectedCountry.id, adminLevel.code);
        }
    };

    handleCitySelect = (city) => {
        this.setState({
            selectedCity: city,
            citySearchTerm: '',
            showCityDropdown: false
        });
        this.fetchApiData(this.state.selectedCountry.id, this.state.selectedAdminLevel.code, city.name[0]);
    };

    getFilteredCountries = () => {
        const { countries, countrySearchTerm } = this.state;
        if (!countrySearchTerm) return countries;

        const searchLower = countrySearchTerm.toLowerCase();
        return countries.filter(country =>
            country.name.some(name => name.toLowerCase().includes(searchLower)) ||
            country.id.toLowerCase().includes(searchLower)
        );
    };

    getFilteredCities = () => {
        const { cities, citySearchTerm } = this.state;
        if (!citySearchTerm) return cities;

        const searchLower = citySearchTerm.toLowerCase();
        return cities.filter(city =>
            city.name.some(name => name.toLowerCase().includes(searchLower)) ||
            city.id.toLowerCase().includes(searchLower)
        );
    };

    render() {
        this.setLocale(this.props.locale);
        const {
            selectedCountry,
            selectedAdminLevel,
            selectedCity,
            apiResponse,
            loadingCountries,
            loadingCities,
            loadingApiData,
            countrySearchTerm,
            citySearchTerm,
            error,
            showCountryDropdown,
            showAdminLevelDropdown,
            showCityDropdown
        } = this.state;

        const filteredCountries = this.getFilteredCountries();
        const filteredCities = this.getFilteredCities();

        return (
            <div className="api-showcase">
                <div className="api-showcase__container">
                    <h2 className="api-showcase__title">{this.text.title}</h2>
                    <p className="api-showcase__subtitle">
                        {this.text.subtitle}
                    </p>

                    {error && (
                        <div className="api-showcase__error">
                            {error}
                        </div>
                    )}

                    <div className="api-showcase__selectors" ref={this.setWrapperRef}>
                        {/* Country Selector */}
                        <div className="api-showcase__selector-group">
                            <label className="api-showcase__label">{this.text.selectCountry}</label>
                            <div className="api-showcase__dropdown">
                                <input
                                    type="text"
                                    className="api-showcase__search-input"
                                    placeholder={selectedCountry ? selectedCountry.name[0] : this.text.searchCountries}
                                    value={countrySearchTerm}
                                    onChange={(e) => this.setState({ countrySearchTerm: e.target.value, showCountryDropdown: true })}
                                    onFocus={() => this.setState({ showCountryDropdown: true, showCityDropdown: false, showAdminLevelDropdown: false })}
                                    onClick={() => this.setState({ showCountryDropdown: true, showCityDropdown: false, showAdminLevelDropdown: false })}
                                    disabled={loadingCountries}
                                />
                                {loadingCountries && (
                                    <div className="api-showcase__loading">
                                        <div className="api-showcase__spinner"></div>
                                    </div>
                                )}
                                {(showCountryDropdown || countrySearchTerm !== '') && !loadingCountries && (
                                    <div className="api-showcase__dropdown-list">
                                        {filteredCountries.length > 0 ? (
                                            filteredCountries.map(country => (
                                                <div
                                                    key={country.id}
                                                    className="api-showcase__dropdown-item"
                                                    onClick={() => this.handleCountrySelect(country)}
                                                >
                                                    <span className="api-showcase__dropdown-code">{country.id}</span>
                                                    <span className="api-showcase__dropdown-name">{country.name[0]}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="api-showcase__dropdown-item api-showcase__dropdown-item--empty">
                                                {this.text.noCountriesFound}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Admin Level Selector */}
                        <div className="api-showcase__selector-group">
                            <label className="api-showcase__label">{this.text.selectAdminLevel}</label>
                            <div className="api-showcase__dropdown">
                                <input
                                    type="text"
                                    className="api-showcase__search-input"
                                    placeholder={selectedAdminLevel ? selectedAdminLevel.name : 'Select admin level...'}
                                    value=""
                                    onFocus={() => this.setState({ showAdminLevelDropdown: true, showCountryDropdown: false, showCityDropdown: false })}
                                    onClick={() => this.setState({ showAdminLevelDropdown: true, showCountryDropdown: false, showCityDropdown: false })}
                                    readOnly
                                    style={{ cursor: 'pointer' }}
                                />
                                {showAdminLevelDropdown && (
                                    <div className="api-showcase__dropdown-list">
                                        {this.getAdminLevels().map(level => (
                                            <div
                                                key={level.code}
                                                className="api-showcase__dropdown-item"
                                                onClick={() => this.handleAdminLevelSelect(level)}
                                            >
                                                <span className="api-showcase__dropdown-name">{level.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* City Selector */}
                        <div className="api-showcase__selector-group">
                            <label className="api-showcase__label">{this.text.selectCity}</label>
                            <div className="api-showcase__dropdown">
                                <input
                                    type="text"
                                    className="api-showcase__search-input"
                                    placeholder={selectedCity ? selectedCity.name[0] : this.text.searchCities}
                                    value={citySearchTerm}
                                    onChange={(e) => this.setState({ citySearchTerm: e.target.value, showCityDropdown: true })}
                                    onFocus={() => this.setState({ showCityDropdown: true, showCountryDropdown: false, showAdminLevelDropdown: false })}
                                    onClick={() => this.setState({ showCityDropdown: true, showCountryDropdown: false, showAdminLevelDropdown: false })}
                                    disabled={!selectedCountry || loadingCities}
                                />
                                {loadingCities && (
                                    <div className="api-showcase__loading">
                                        <div className="api-showcase__spinner"></div>
                                    </div>
                                )}
                                {(showCityDropdown || citySearchTerm !== '') && !loadingCities && selectedCountry && (
                                    <div className="api-showcase__dropdown-list">
                                        {filteredCities.length > 0 ? (
                                            filteredCities.slice(0, 100).map(city => (
                                                <div
                                                    key={city.id}
                                                    className="api-showcase__dropdown-item"
                                                    onClick={() => this.handleCitySelect(city)}
                                                >
                                                    <span className="api-showcase__dropdown-name">{city.name[0]}</span>
                                                    <span className="api-showcase__dropdown-code">{city.id}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="api-showcase__dropdown-item api-showcase__dropdown-item--empty">
                                                {this.text.noCitiesFound}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* API Response Display */}
                    {(loadingApiData || apiResponse) && (
                        <div className="api-showcase__response">
                            <div className="api-showcase__response-header">
                                <h3>{this.text.apiResponse}</h3>
                                {selectedCountry && selectedCity && selectedAdminLevel && (
                                    <span className="api-showcase__response-endpoint">
                                        GET /territory/{selectedCountry.id}/{selectedAdminLevel.code}/name/{selectedCity.name[0]}
                                    </span>
                                )}
                            </div>
                            {loadingApiData ? (
                                <div className="api-showcase__loading api-showcase__loading--response">
                                    <div className="api-showcase__spinner"></div>
                                    <p>{this.text.loadingApiData}</p>
                                </div>
                            ) : (
                                <div>
                                    <div className="api-showcase__raw-json">
                                        <h4>JSON Response</h4>
                                        <pre className="api-showcase__json">
                                            {JSON.stringify(apiResponse, null, 2)}
                                        </pre>
                                    </div>
                                    {/* <AQVisualizer response={apiResponse} locale={this.props.locale} /> */}
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="api-showcase__footer-note">
                        <p dangerouslySetInnerHTML={{ __html: this.text.footerNote }}></p>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        locale: state.locale.value,
    };
};

export default connect(mapStateToProps, null)(ApiShowcase);



// WEBPACK FOOTER //
// ./src/components/api-showcase/ApiShowcase.js